/**
 * Redux thunk action creators for the Organization feature.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as OrganizationApi from '../facades/organizationApi';
import { ApiErrorPayload, toApiErrorPayload } from '../facades/apiFacade';
import { AdminInvitation, Organization, OrgMember, OrganizationMemberRole, OrgRole } from '../../@types/organization';
import type { ThunkAPI } from '../stores/store';

type OrgThunkAPI = ThunkAPI & { rejectValue: ApiErrorPayload };

export const fetchMyOrganization = createAsyncThunk<Organization, { accessToken: string }, OrgThunkAPI>(
    'organization/fetchMyOrganization',
    async ({ accessToken }, thunkApi) => {
        try {
            return await OrganizationApi.getMyOrganization(accessToken);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const createOrganization = createAsyncThunk<Organization, { accessToken: string; name: string }, OrgThunkAPI>(
    'organization/createOrganization',
    async ({ accessToken, name }, thunkApi) => {
        try {
            return await OrganizationApi.createOrganization(accessToken, name);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const renameOrganization = createAsyncThunk<Organization, { accessToken: string; name: string }, OrgThunkAPI>(
    'organization/renameOrganization',
    async ({ accessToken, name }, thunkApi) => {
        try {
            return await OrganizationApi.renameOrganization(accessToken, name);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const fetchOrganizationMembers = createAsyncThunk<OrgMember[], { accessToken: string }, OrgThunkAPI>(
    'organization/fetchOrganizationMembers',
    async ({ accessToken }, thunkApi) => {
        try {
            return await OrganizationApi.getOrganizationMembers(accessToken);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const updateOrganizationMemberRole = createAsyncThunk<
    OrgMember,
    { accessToken: string; userObjectId: string; role: OrganizationMemberRole },
    OrgThunkAPI
>('organization/updateOrganizationMemberRole', async ({ accessToken, userObjectId, role }, thunkApi) => {
    try {
        return await OrganizationApi.updateOrganizationMemberRole(accessToken, userObjectId, role);
    } catch (error) {
        return thunkApi.rejectWithValue(toApiErrorPayload(error));
    }
});

export const removeOrganizationMember = createAsyncThunk<
    string,
    { accessToken: string; userObjectId: string },
    OrgThunkAPI
>('organization/removeOrganizationMember', async ({ accessToken, userObjectId }, thunkApi) => {
    try {
        await OrganizationApi.removeOrganizationMember(accessToken, userObjectId);
        return userObjectId;
    } catch (error) {
        return thunkApi.rejectWithValue(toApiErrorPayload(error));
    }
});

export const fetchOrganizationInvitations = createAsyncThunk<AdminInvitation[], { accessToken: string }, OrgThunkAPI>(
    'organization/fetchOrganizationInvitations',
    async ({ accessToken }, thunkApi) => {
        try {
            return await OrganizationApi.getOrganizationInvitations(accessToken);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const createOrganizationInvitation = createAsyncThunk<
    AdminInvitation,
    { accessToken: string; email: string; role: OrgRole },
    OrgThunkAPI
>('organization/createOrganizationInvitation', async ({ accessToken, email, role }, thunkApi) => {
    try {
        return await OrganizationApi.createOrganizationInvitation(accessToken, email, role);
    } catch (error) {
        return thunkApi.rejectWithValue(toApiErrorPayload(error));
    }
});

export const revokeOrganizationInvitation = createAsyncThunk<
    string,
    { accessToken: string; invitationObjectId: string },
    OrgThunkAPI
>('organization/revokeOrganizationInvitation', async ({ accessToken, invitationObjectId }, thunkApi) => {
    try {
        await OrganizationApi.revokeOrganizationInvitation(accessToken, invitationObjectId);
        return invitationObjectId;
    } catch (error) {
        return thunkApi.rejectWithValue(toApiErrorPayload(error));
    }
});
